import { lazy, ReactNode } from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MyAssessmentIcon from "@assets/SVGIcons/MyAssessmentIcon.web";
import MyCoursesIcon from "@assets/SVGIcons/MyCoursesIcon.web";
import MyGradebookIcon from "@assets/SVGIcons/MyGradebookIcon.web";
import MyPathIcon from "@assets/SVGIcons/MyPathIcon.web";
import StudentProfile from "@components/pages/studentWorkSpace/profile/StudentProfile";
import { NavigationGroup } from ".";
import DashboardIcon from "@assets/SVGIcons/DashboardIcon.web";
import { Navigate } from "react-router-dom";
// AutoStoriesIcon, SmartToyIcon, BubbleChartIcon removed with Portfolio,
// AI Tutor, and Knowledge Graph nav entries per Chronicle boundary (2026-04-15).
// These features are Chronicle-exclusive; see docs/CHRONICLE-BOUNDARY.md.

type Props = {
  path: string;
  label: string;
  component: ReactNode;
  icon?: ReactNode;
};

// LAZY ROUTES
const MyCourses = lazy(() => import("@components/pages/studentWorkSpace/courses/MyCourses"),);
const MyPath = lazy(() => import("@components/pages/studentWorkSpace/path/MyPath"));
const MyPathToday = lazy(() => import("@components/pages/studentWorkSpace/path/tabs/TodayTab"))
const MyPathOverdue = lazy(() => import("@components/pages/studentWorkSpace/path/tabs/OverdueTab"))
const MyPathUpcoming = lazy(() => import("@components/pages/studentWorkSpace/path/tabs/UpcomingTab"))
const MyPathReview = lazy(() => import("@components/pages/studentWorkSpace/path/tabs/ReviewTab"))
const MyAssessment = lazy(() => import("@components/pages/studentWorkSpace/assessment/MyAssessment"),);
const MyAssessmentDueTab = lazy(() => import("@components/pages/studentWorkSpace/assessment/tabs/DueAssessmentTab"));
const MyAssessmentCompletedTab = lazy(() => import("@components/pages/studentWorkSpace/assessment/tabs/CompletedAssessmentTab"));
const MyAssessmentGradedTab = lazy(() => import("@components/pages/studentWorkSpace/assessment/tabs/GradedAssessmentTab"));
const AssessmentDetails = lazy(() => import("@components/pages/studentWorkSpace/assessment/AssessmentDetails"));
const AssessmentQuestionaire = lazy(() => import("@components/pages/studentWorkSpace/assessment/AssessmentQuestionaire"));
const MyGradeBook = lazy(() => import("@components/pages/studentWorkSpace/gradebook/MyGradeBook"),);
const MyCalendar = lazy(() => import("@components/pages/studentWorkSpace/calendar/StudentCalendar"));
const StudentDashboard = lazy(() => import("@components/pages/studentWorkSpace/dashboard/StudentDashboard"));
const MyCourseJourney = lazy(() => import("@components/pages/studentWorkSpace/courses/MyCourseJourney"));
// MyPortfolio, AiTutor, KnowledgeGraphPage imports removed per Chronicle boundary (2026-04-15).
// Components preserved intact at their original paths as Chronicle incubator
// material for future Chronicle service implementation. Do not delete.
const PrintUnofficialReportCard = lazy(() => import("@components/pages/studentWorkSpace/reportcard/PrintUnofficialReportCard"));

// Chronicle integration — iframe pages mounted when org.chronicle_enabled.
// See docs/superpowers/specs/2026-04-27-chronicle-integration-design.md.
// Native Grove tutor UI (Indigo Observatory) replaces the Chronicle iframe page
// at chronicle/tutor. The iframe page stays on disk for reference.
const GroveTutor = lazy(() => import("@components/pages/studentWorkSpace/tutor/GroveTutor"));
const ChronicleWork = lazy(() => import("@components/pages/chronicle/student/WorkPage"));
const ChronicleEvidence = lazy(() => import("@components/pages/chronicle/student/EvidencePage"));

// STAGING-ONLY tab reveal (go-live visibility gates). Hides this nav tab from
// the production sidebar while keeping the route registered, so a direct
// link/deep link still resolves. VITE_STAGE2_TABS_UNLOCK is set to "true"
// only on the FE Vercel Preview (staging) env — the production build (var
// absent) keeps hidden:true. Mirrors VITE_PT_UNLOCK_TABS in
// parentTeacherRoutes.tsx.
const STAGE2_TABS_UNLOCKED = import.meta.env.VITE_STAGE2_TABS_UNLOCK === "true";

export const studentRoutes: Props[] = [
  {
    path: "profile",
    label: "profile",
    component: <StudentProfile />,
  },
  {
    path: "my-courses/:course_id/journey",
    label: "Course Journey",
    component: <MyCourseJourney />,
  },
  {
    path: "my-assessments/details/:assessment_id",
    label: "Assessment Details",
    component: <AssessmentDetails />
  },
  {
    path: "my-assessments/questionaire/:assessment_id",
    label: "Assessment Questionaire",
    component: <AssessmentQuestionaire />
  },
  {
    path: "report-card",
    label: "Report Card",
    component: <PrintUnofficialReportCard />,
  },
  // Chronicle iframe routes — degrade gracefully when not configured.
  {
    path: "chronicle/tutor",
    label: "Orbit",
    component: <GroveTutor />,
  },
  {
    path: "chronicle/work",
    label: "Work",
    component: <ChronicleWork />,
  },
  {
    path: "chronicle/evidence",
    label: "Submit Evidence",
    component: <ChronicleEvidence />,
  },
  // added here nested routes from student role
];

export const studentNavigations: NavigationGroup[] = [
  {
    title: "Getting Started",
    routes: [
      {
        label: 'dashboard',
        component: <StudentDashboard />,
        icon: <DashboardIcon fontSize="small"/>,
        index: true,
        path: 'dashboard'
      },
    ]
  },
  {
    title: "Work Space",
    routes: [
      {
        path: "my-courses",
        label: "my courses",
        component: <MyCourses />,
        icon: <MyCoursesIcon fontSize="medium" />,
      },
      {
        path: "my-path",
        label: "my path",
        component: <MyPath />,
        icon: <MyPathIcon fontSize="medium" />,
        hidden: !STAGE2_TABS_UNLOCKED,
        children: [
          {
            index: true,
            component: <Navigate to="today"/>
          },
          {
            path: "today",
            label: "Today",
            component: <MyPathToday />
          },
          {
            path: "overdue",
            label: "Overdue",
            component: <MyPathOverdue />
          },
          {
            path: "upcoming",
            label: "Upcoming",
            component: <MyPathUpcoming />
          },
          {
            path: "review",
            label: "Review",
            component: <MyPathReview/>
          },
        ]
      },
      {
        path: "my-assessments",
        label: "my assessment",
        component: <MyAssessment />,
        icon: <MyAssessmentIcon fontSize="medium"/>,
        children: [
          {
            index: true,
            component: <MyAssessmentDueTab />
          },
          {
            path: 'completed',
            component: <MyAssessmentCompletedTab />
          },
          {
            path: 'graded',
            component: <MyAssessmentGradedTab/>
          }
        ]
      },
      {
        path: "my-gradebook",
        label: "my grade book",
        component: <MyGradeBook />,
        icon: <MyGradebookIcon fontSize="medium"/>,
      },
      {
        // Calendar Real-Build P1b: read-only render of the student's own
        // events + lessons (reuses TeacherCalendar in role="student" — see
        // studentWorkSpace/calendar/StudentCalendar.tsx). Same icon as the
        // teacher/parent-teacher "calendar" nav entries for consistency.
        path: "my-calendar",
        label: "my calendar",
        component: <MyCalendar />,
        icon: <MyPathIcon fontSize="medium" />,
      },
      {
        path: "chronicle/tutor",
        label: "Orbit",
        component: <GroveTutor />,
        icon: <AutoAwesomeIcon fontSize="small" />,
        // Pre-release: hidden in prod, shown where the tutor backend is enabled.
        requiresAiTutor: true,
      },
      // "my-portfolio" nav entry removed per Chronicle boundary (2026-04-15).
      // Portfolio & Credentials is a Chronicle-exclusive feature; when Chronicle
      // is enabled, it will appear in the Chronicle section. Component code
      // preserved at components/pages/studentWorkSpace/portfolio/ as incubator.
    ]
  },
  // "AI Tools" nav group (AI Tutor + Knowledge Graph) removed per Chronicle
  // boundary (2026-04-15). Both features are Chronicle-exclusive and will
  // appear in the Chronicle section when Chronicle is integrated. Component
  // code preserved at components/pages/shared/ai/ and components/pages/
  // studentWorkSpace/knowledgeGraph/ as incubator material.
];


export default studentNavigations;
